"use client";

import { useEffect, useRef } from "react";

// ============================================================================
// Intention de sortie — une seule fois par session.
//   Desktop : la souris quitte la fenêtre par le haut (vers les onglets / la
//   barre d'adresse). Mobile : pas de souris, on prend un retour rapide vers
//   le haut après avoir parcouru la page (le geste qui précède le « retour »).
//   Armée après ARM_DELAY_MS, pour ne pas surgir sur un visiteur qui arrive.
// ============================================================================

const ARM_DELAY_MS = 6000;
const KEY = "exit_intent_shown";

export function useExitIntent(onExit: () => void, enabled: boolean) {
  const cb = useRef(onExit);
  cb.current = onExit;

  useEffect(() => {
    if (!enabled) return;
    try {
      if (sessionStorage.getItem(KEY)) return;
    } catch {
      /* stockage indisponible : on arme quand même */
    }

    let armed = false;
    let done = false;
    const fire = () => {
      if (!armed || done) return;
      done = true;
      try {
        sessionStorage.setItem(KEY, "1");
      } catch {
        /* idem */
      }
      cb.current();
    };

    const onMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) fire();
    };

    // Mobile : remontée de plus de 25 % d'écran en moins de 400 ms, après
    // être descendu d'au moins un écran.
    let lastY = window.scrollY;
    let lastT = performance.now();
    let maxY = lastY;
    const onScroll = () => {
      const y = window.scrollY;
      const t = performance.now();
      maxY = Math.max(maxY, y);
      const up = lastY - y;
      if (maxY > window.innerHeight && up > window.innerHeight * 0.25 && t - lastT < 400) fire();
      if (t - lastT > 120) {
        lastY = y;
        lastT = t;
      }
    };

    const armTimer = window.setTimeout(() => (armed = true), ARM_DELAY_MS);
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled]);
}
